import PropTypes from 'prop-types';
import React from 'react';
import Link from 'next/link';
import { isPeriod } from '../../../helper';
import { LongArrowRight } from '../../icons/arrow/arrow';

import StyledHeroTeaser from './heroTeaser.style';
import EventDate from '../event-date/eventDate';
import Headline from '../../atoms/headline/headline';
import Container from '../../layout/container/container';
import FilterTags from '../../atoms/filter-tags/filterTags';
import Copy from '../../atoms/copy/copy';
import Picture from '../../atoms/picture/picture';
import Tags from '../../atoms/tags/tags';
import DigitalContentIcon from '../../icons/digitalContent.svg';

const HeroTeaser = props => {
  const {
    teaserType,
    uri,
    headline,
    subline,
    copy,
    startDate,
    endDate,
    pageTags,
    typeTags = [],
    image
  } = props;

  const isEvent = teaserType === 'event';
  const period = startDate && endDate && isPeriod(startDate, endDate);

  // Check if typeTags contain tags starting with #futurium
  // then show digital content icon
  const isDigitalContent =
    typeTags &&
    typeTags.length > 0 &&
    typeTags.filter(item => {
      return item.title.toLowerCase().startsWith('#futurium');
    }).length > 0;

  return (
    <StyledHeroTeaser {...props} isEvent={isEvent} hasImage={image} period={period}>
      <Container>
        <Link
          href={{
            pathname: `/universal`,
            query: { uri }
          }}
          as={uri}
          className="heroTeaser__link"
        >
          <div className="heroTeaser__content">
            <div className="heroTeaser__header">
              {pageTags && pageTags.length > 0 && (
                <FilterTags tags={pageTags} className="heroTeaser__categories" />
              )}

              <div className="heroTeaser__headlines">
                {isDigitalContent && (
                  <DigitalContentIcon className="heroTeaser__icon" width="58px" height="50px" />
                )}
                <div className="heroTeaser__headlines-inner">
                  {subline && (
                    <Headline tag="h3" subline className="heroTeaser__subline">
                      {subline}
                    </Headline>
                  )}
                  <Headline tag="h2" primary className="heroTeaser__headline">
                    {headline}
                  </Headline>
                  {!copy && typeTags && (
                    <Tags className="heroTeaser__tags" limit={3} tags={typeTags} />
                  )}
                </div>
              </div>
            </div>
            {image && (
              <div className="heroTeaser__image-wrapper">
                <Picture {...image} className="heroTeaser__image" />

                {isEvent && (
                  <EventDate
                    className="heroTeaser__date"
                    period={period}
                    startDate={startDate}
                    endDate={endDate}
                    isTeaser
                  />
                )}
              </div>
            )}
            {copy && (
              <div className="heroTeaser__text">
                <Copy className="heroTeaser__copy">{copy}</Copy>
                {typeTags && !isEvent && (
                  <Tags className="heroTeaser__tags" limit={3} tags={typeTags} />
                )}
                <div className="heroTeaser__arrow">
                  <LongArrowRight role="presentation" />
                </div>
              </div>
            )}
          </div>
          {isEvent && !image && (
            <EventDate
              className="heroTeaser__date"
              period={period}
              startDate={startDate}
              endDate={endDate}
            />
          )}
        </Link>
      </Container>
    </StyledHeroTeaser>
  );
};

HeroTeaser.propTypes = {
  teaserType: PropTypes.oneOf(['event', 'article']).isRequired,
  uri: PropTypes.string.isRequired,
  headline: PropTypes.string.isRequired,
  subline: PropTypes.string,
  copy: PropTypes.string,
  startDate: PropTypes.shape({
    d: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    m: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    y: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  }),
  endDate: PropTypes.shape({
    d: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    m: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    y: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  }),
  pageTags: PropTypes.arrayOf(PropTypes.shape()),
  typeTags: PropTypes.arrayOf(PropTypes.shape()),
  image: PropTypes.shape()
};

export default HeroTeaser;
